import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

/** Même drapeau que `App.tsx` : l'assistant n'existe que sur DEMO. */
const isDemo = import.meta.env.VITE_IS_DEMO === "true";

interface AssistantContextValue {
  /**
   * L'assistant existe-t-il sur cet environnement ? Faux en production : rien
   * ne doit alors afficher de bouton d'ouverture, sous peine d'offrir un geste
   * qui n'aboutit nulle part.
   */
  available: boolean;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

/**
 * L'état d'ouverture de l'assistant, sorti de `Chatbot`.
 *
 * Il y vivait en `useState` local, ce qui rendait le composant seul maître de
 * son ouverture : il fallait donc son propre bouton flottant. Le shell
 * « direction A » ouvre l'assistant depuis l'en-tête — l'état doit être
 * au-dessus des deux.
 */
export function AssistantProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const value = useMemo(
    () => ({ available: isDemo, isOpen: isDemo && isOpen, open, close, toggle }),
    [isOpen, open, close, toggle],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

/**
 * Rend un objet inerte hors du provider plutôt que de lever : un layout monté
 * dans un banc de rendu ou un test ne doit pas tomber parce que l'assistant
 * n'est pas là.
 */
export function useAssistant(): AssistantContextValue {
  return (
    useContext(AssistantContext) ?? {
      available: false,
      isOpen: false,
      open: () => {},
      close: () => {},
      toggle: () => {},
    }
  );
}
